import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import configService from '../services/configService';

const LiveCallView = ({ sessionId: propSessionId, token: propToken }) => {
    const { sessionId: paramSessionId } = useParams();
    const sessionId = propSessionId || paramSessionId;
    const navigate = useNavigate();
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    
    const [messages, setMessages] = useState([]);
    const [status, setStatus] = useState('connecting');
    const [error, setError] = useState(null);
    const [callerPhone, setCallerPhone] = useState(null);
    const [isTransferring, setIsTransferring] = useState(false);
    const [handoffStage, setHandoffStage] = useState(null); // null, 'guidance', 'ready'
    const [placeholderIds, setPlaceholderIds] = useState(new Set());
    const [whisperText, setWhisperText] = useState('');
    const [isSendingWhisper, setIsSendingWhisper] = useState(false);
    const [whisperSuccess, setWhisperSuccess] = useState(false);
    const [showWhisperHelp, setShowWhisperHelp] = useState(false);
    const intervalRef = useRef(null);
    const messagesEndRef = useRef(null);
    const whisperInputRef = useRef(null);

    // Extract and store Cognito token immediately (synchronous)
    const token = propToken || searchParams.get('token');
    const [cognitoToken] = useState(() => {
        if (token) {
            try {
                // Validate token before storing
                const payload = JSON.parse(atob(token.split('.')[1]));
                if (payload.exp * 1000 > Date.now()) {
                    localStorage.setItem('cognitoAccessToken', token);
                    return token;
                } else {
                    navigate('/login');
                    return null;
                }
            } catch (e) {
                navigate('/login');
                return null;
            }
        }
        return null;
    });

    useEffect(() => {
        // Allow access with either Cognito token from URL or regular user session
        if (!cognitoToken && !user) {
            localStorage.setItem('redirectAfterLogin', window.location.pathname + window.location.search);
            navigate('/login');
            return;
        }

        // Set up token expiry check for live call tokens
        let tokenExpiryCheck;
        if (cognitoToken) {
            try {
                const payload = JSON.parse(atob(cognitoToken.split('.')[1]));
                const expiryTime = payload.exp * 1000;
                const timeUntilExpiry = expiryTime - Date.now();
                
                if (timeUntilExpiry > 0) {
                    tokenExpiryCheck = setTimeout(() => {
                        localStorage.removeItem('cognitoAccessToken');
                        navigate('/login');
                    }, timeUntilExpiry);
                }
            } catch (e) {
                console.error('Error setting up token expiry check:', e);
            }
        }

        const fetchConversationFromAPI = async () => {
            try {
                const conversation = await configService.getConversationDetails(sessionId);
                const rawMessages = conversation.turns || [];
                const processedMessages = addPlaceholders(rawMessages);
                setMessages(processedMessages);
                
                // Check if call has ended
                if (conversation.end_time) {
                    setStatus('ended');
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                    }
                } else {
                    setStatus(rawMessages.length > 0 ? 'active' : 'waiting');
                }
                
                // Extract caller phone
                if (!callerPhone && conversation.from_number) {
                    const cleaned = conversation.from_number.replace(/^\+1/, '').replace(/\D/g, '');
                    if (cleaned.length === 10) {
                        setCallerPhone(`${cleaned.slice(0,3)}-${cleaned.slice(3,6)}-${cleaned.slice(6)}`);
                    } else {
                        setCallerPhone(conversation.from_number);
                    }
                }
                
                setError(null);
            } catch (err) {
                console.error('Failed to fetch conversation:', err);
                if (err.message.includes('404')) {
                    setMessages([]);
                    setStatus('waiting');
                    setError(null);
                } else {
                    setError('Failed to load conversation');
                    setStatus('error');
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                    }
                }
            }
        };

        fetchConversationFromAPI();
        intervalRef.current = setInterval(fetchConversationFromAPI, 1000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            if (tokenExpiryCheck) {
                clearTimeout(tokenExpiryCheck);
            }
        };
    }, [sessionId, user, navigate, callerPhone, cognitoToken]);

    const addPlaceholders = (rawMessages) => {
        // Filter out system messages except transfer events
        const filtered = rawMessages.filter(msg => 
            msg.direction !== 'system' || 
            (msg.content?.event === 'transfer')
        );
        const sorted = filtered.sort((a, b) => (a.turn_timestamp || 0) - (b.turn_timestamp || 0));
        
        if (status !== 'active') {
            return sorted;
        }
        
        // Build placeholder map: { placeholderId: { before, after, shouldExist } }
        const placeholderMap = new Map();
        
        for (let i = 1; i < sorted.length; i++) {
            const prev = sorted[i-1];
            const current = sorted[i];
            
            if (prev.direction === 'assistant' && current.direction === 'assistant') {
                const placeholderId = `placeholder_${prev.turn_timestamp}_${current.turn_timestamp}`;
                placeholderMap.set(placeholderId, {
                    before: prev.turn_timestamp,
                    after: current.turn_timestamp,
                    shouldExist: true
                });
            }
        }
        
        // Check if user messages invalidate placeholders
        sorted.forEach(msg => {
            if (msg.direction === 'user') {
                placeholderMap.forEach((placeholder, id) => {
                    if (msg.turn_timestamp > placeholder.before && msg.turn_timestamp < placeholder.after) {
                        placeholder.shouldExist = false;
                    }
                });
            }
        });
        
        // Create placeholders that should exist
        const result = [...sorted];
        const newPlaceholderIds = new Set();
        
        placeholderMap.forEach((placeholder, placeholderId) => {
            if (placeholder.shouldExist) {
                const placeholderTime = placeholder.before === placeholder.after 
                    ? placeholder.after - 500 
                    : placeholder.after - 1000;
                result.push({
                    direction: 'user',
                    turn_timestamp: placeholderTime,
                    content: { text: 'processing...' },
                    isPlaceholder: true,
                    placeholderId,
                    prevAssistantTime: placeholder.before,
                    nextAssistantTime: placeholder.after
                });
                newPlaceholderIds.add(placeholderId);
            }
        });
        
        const finalResult = result.sort((a, b) => (a.turn_timestamp || 0) - (b.turn_timestamp || 0));
        setPlaceholderIds(newPlaceholderIds);
        return finalResult;
    };

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const handleTakeOver = async () => {
        setIsTransferring(true);
        try {
            // Extract staff phone from URL if present
            const staffPhone = searchParams.get('phone');
            const response = await configService.transferCall(sessionId, staffPhone);
            if (response.success) {
                setStatus('transferred');
            } else {
                // Check for race condition error
                if (response.error && response.error.includes('already being transferred')) {
                    setError('Another staff member is already taking this call');
                } else {
                    setError(response.error || 'Transfer failed');
                }
            }
        } catch (err) {
            if (err.message && err.message.includes('409')) {
                setError('Another staff member is already taking this call');
            } else {
                setError('Transfer failed: ' + err.message);
            }
        } finally {
            setIsTransferring(false);
        }
    };

    const handleHandoff = async () => {
        if (!handoffStage) {
            // Stage 1: Inject guidance
            try {
                await configService.injectFrontendGuidance(sessionId, 
                    "I'm going to transfer you to someone who can help you with your request. Please hold for just a moment."
                );
                setHandoffStage('guidance');
                // Start polling for user acknowledgment
                startPollingForAcknowledgment();
            } catch (err) {
                setError('Failed to initiate handoff: ' + err.message);
            }
        } else if (handoffStage === 'ready') {
            // Stage 2: Execute transfer
            setIsTransferring(true);
            try {
                // Extract staff phone from URL if present
                const staffPhone = searchParams.get('phone');
                const response = await configService.transferCall(sessionId, staffPhone);
                if (response.success) {
                    setStatus('transferred');
                    setHandoffStage(null);
                } else {
                    setError(response.error || 'Transfer failed');
                }
            } catch (err) {
                setError('Transfer failed: ' + err.message);
            } finally {
                setIsTransferring(false);
            }
        }
    };

    const startPollingForAcknowledgment = () => {
        const checkInterval = setInterval(async () => {
            try {
                const conversation = await configService.getConversationDetails(sessionId);
                const lastTurn = conversation.turns?.[conversation.turns.length - 1];
                
                if (lastTurn?.direction === 'user' && handoffStage === 'guidance') {
                    // User acknowledged!
                    setHandoffStage('ready');
                    clearInterval(checkInterval);
                }
            } catch (err) {
                // Ignore polling errors
            }
        }, 1000);

        // Auto-cleanup after 30 seconds
        setTimeout(() => {
            clearInterval(checkInterval);
            if (handoffStage === 'guidance') {
                setHandoffStage('ready'); // Allow manual completion
            }
        }, 30000);
    };

    const handleWhisperSend = async () => {
        if (!whisperText.trim() || isSendingWhisper || status === 'ended') {
            return;
        }

        setIsSendingWhisper(true);
        setError(null);

        try {
            await configService.injectFrontendGuidance(sessionId, whisperText.trim());
            setWhisperText('');
            setWhisperSuccess(true);
            
            // Clear success indicator after 2 seconds
            setTimeout(() => setWhisperSuccess(false), 2000);
        } catch (err) {
            setError('Failed to send instruction: ' + err.message);
        } finally {
            setIsSendingWhisper(false);
        }
    };

    const handleWhisperKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleWhisperSend();
        }
    };



    // Require either Cognito token or user session
    if (!cognitoToken && !user) {
        return <div>Please log in to view live conversations.</div>;
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full h-full flex flex-col">
                {/* Header */}
                <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <h3 className="text-lg font-semibold text-gray-900">Live Call</h3>
                        <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 rounded-full ${
                                status === 'active' ? 'bg-green-500 animate-pulse' : 
                                status === 'waiting' ? 'bg-yellow-500' :
                                status === 'ended' ? 'bg-gray-500' : 'bg-red-500'
                            }`}></div>
                            <span className="text-sm font-medium capitalize">
                                {status === 'waiting' ? 'Waiting for call' : status}
                            </span>
                        </div>
                    </div>
                    <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-gray-600">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                    {/* Error Message */}
                    {error && (
                        <div className="bg-red-50 border-l-4 border-red-400 p-4 mx-6 mt-4">
                            <div className="flex">
                                <div className="ml-3">
                                    <p className="text-sm text-red-700">{error}</p>
                                </div>
                            </div>
                        </div>
                    )}

                {/* Messages */}
                <div className="flex-1 px-6 py-4 overflow-y-auto bg-gray-50 shadow-inner">
                        {messages.length === 0 ? (
                            <div className="text-center text-gray-500 py-8">
                                {status === 'connecting' ? 'Connecting to live conversation...' : 
                                 status === 'waiting' ? 'Waiting for conversation to start...' :
                                 status === 'connected' ? 'Waiting for conversation to start...' :
                                 'No messages yet'}
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {messages
                                    .filter(turn => turn.content?.role !== 'system' || turn.content?.event === 'transfer')
                                    .map((turn, index) => {
                                        const isUser = turn.direction === 'user';
                                        const isSystem = turn.direction === 'system';
                                        const isPlaceholder = turn.isPlaceholder;
                                        const isSMS = turn.channel === 'sms';
                                        const textContent = turn.content?.text || turn.content?.content || 'No text content';
                                        
                                        // System transfer message
                                        if (isSystem && turn.content?.event === 'transfer') {
                                            return (
                                                <div key={index} className="flex justify-center">
                                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-2 text-sm text-yellow-800 flex items-center gap-2">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                                        </svg>
                                                        <span className="font-medium">{textContent}</span>
                                                    </div>
                                                </div>
                                            );
                                        }
                                        
                                        return (
                                            <div key={turn.placeholderId || index} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                                                <div className={`max-w-xs sm:max-w-md rounded-lg px-3 py-2 ${
                                                    isUser 
                                                        ? (isPlaceholder ? 'bg-[#e8f4ff] border-l-3 border-[#055efd] opacity-70' : 'bg-[#e8f4ff] border-l-3 border-[#055efd]')
                                                        : 'bg-[#f8f9fa] border-l-3 border-[#6c757d]'
                                                }`} style={{borderLeftWidth: '3px'}}>
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className={`text-xs font-semibold uppercase ${
                                                            isUser ? 'text-[#055efd]' : 'text-[#6c757d]'
                                                        }`}>
                                                            {isUser ? 'user' : 'assistant'}
                                                        </span>
                                                        {isSMS && (
                                                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                                                                isUser 
                                                                    ? 'bg-blue-400 text-blue-50' 
                                                                    : 'bg-purple-100 text-purple-700'
                                                            }`}>
                                                                📱 SMS
                                                            </span>
                                                        )}
                                                    </div>
                                                    {isPlaceholder ? (
                                                        <div className="flex space-x-1">
                                                            <div className="w-2 h-2 bg-[#055efd] rounded-full animate-bounce"></div>
                                                            <div className="w-2 h-2 bg-[#055efd] rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                                                            <div className="w-2 h-2 bg-[#055efd] rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                                                        </div>
                                                    ) : (
                                                        <p className="text-sm leading-relaxed text-[#222324]">
                                                            {textContent}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                
                                {/* Listening indicator for active calls */}
                                {status === 'active' && (() => {
                                    const lastMessage = messages[messages.length - 1];
                                    const lastWasUser = lastMessage?.direction === 'user';
                                    const nextSpeaker = lastWasUser ? 'Assistant' : (callerPhone || 'Caller');
                                    const isAssistantNext = lastWasUser;
                                    
                                    return (
                                        <div className={`flex ${isAssistantNext ? 'justify-start' : 'justify-end'}`}>
                                            <div className={`rounded-2xl px-4 py-3 ${
                                                isAssistantNext 
                                                    ? 'bg-gray-100 rounded-bl-md' 
                                                    : 'bg-blue-500 rounded-br-md'
                                            }`}>
                                                <div className="flex items-center gap-1">
                                                    <span className={`text-xs font-medium ${
                                                        isAssistantNext ? 'text-gray-500' : 'text-white'
                                                    }`}>
                                                        {nextSpeaker}
                                                    </span>
                                                    <span className={`text-xs ${
                                                        isAssistantNext ? 'text-gray-400' : 'text-white'
                                                    }`}>
                                                        listening...
                                                    </span>
                                                </div>
                                                <div className="flex space-x-1 mt-1">
                                                    <div className={`w-2 h-2 rounded-full animate-bounce ${
                                                        isAssistantNext ? 'bg-gray-400' : 'bg-white'
                                                    }`}></div>
                                                    <div className={`w-2 h-2 rounded-full animate-bounce ${
                                                        isAssistantNext ? 'bg-gray-400' : 'bg-white'
                                                    }`} style={{animationDelay: '0.1s'}}></div>
                                                    <div className={`w-2 h-2 rounded-full animate-bounce ${
                                                        isAssistantNext ? 'bg-gray-400' : 'bg-white'
                                                    }`} style={{animationDelay: '0.2s'}}></div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}
                                
                                <div ref={messagesEndRef} />
                            </div>
                        )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 px-6 py-4 space-y-4 bg-white shadow-lg">
                    {/* Whisper Input */}
                    {status === 'active' && (
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                    <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                    </svg>
                                    Whisper to Assistant
                                </label>
                                <button
                                    onClick={() => setShowWhisperHelp(!showWhisperHelp)}
                                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-purple-600 transition-colors"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    {showWhisperHelp ? 'Hide help' : 'Show help'}
                                </button>
                            </div>
                            
                            {showWhisperHelp && (
                                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-900 space-y-2">
                                    <p className="font-medium">How Whisper Mode works:</p>
                                    <ul className="list-disc list-inside space-y-1 text-xs">
                                        <li>Send instructions that only the AI can see</li>
                                        <li>The customer won't see your message</li>
                                        <li>AI will follow your instruction in its next response</li>
                                        <li>Use for: asking specific questions, offering promotions, confirming details</li>
                                    </ul>
                                    <p className="text-xs italic">Example: "Ask for their email address" or "Offer the premium package"</p>
                                </div>
                            )}
                            
                            <div className="flex gap-2">
                                <input
                                    ref={whisperInputRef}
                                    type="text"
                                    value={whisperText}
                                    onChange={(e) => setWhisperText(e.target.value)}
                                    onKeyPress={handleWhisperKeyPress}
                                    placeholder="e.g., Ask for their email address, Offer the premium package, Confirm appointment time..."
                                    disabled={isSendingWhisper || status === 'ended'}
                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
                                />
                                <button
                                    onClick={handleWhisperSend}
                                    disabled={!whisperText.trim() || isSendingWhisper || status === 'ended'}
                                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center justify-center ${
                                        whisperSuccess
                                            ? 'bg-green-500 text-white'
                                            : !whisperText.trim() || isSendingWhisper || status === 'ended'
                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            : 'bg-purple-600 hover:bg-purple-700 text-white shadow-md hover:shadow-lg'
                                    }`}
                                >
                                    {isSendingWhisper ? (
                                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                    ) : whisperSuccess ? (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    ) : (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex items-center justify-between">
                        <div className="flex gap-3">
                                <button
                                    className={`font-medium px-4 py-2 rounded-lg shadow-lg ${
                                        status === 'ended' || isTransferring
                                            ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                                            : 'bg-red-500 hover:bg-red-600 text-white'
                                    }`}
                                    style={{
                                        animation: status !== 'ended' && !isTransferring ? 'pulse-reverse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' : 'none'
                                    }}
                                    onClick={handleTakeOver}
                                    disabled={status === 'ended' || isTransferring}
                                >
                                    <style jsx>{`
                                        @keyframes pulse-reverse {
                                            0%, 100% {
                                                background-color: rgb(239 68 68);
                                                color: rgb(255 255 255);
                                            }
                                            50% {
                                                background-color: rgb(185 28 28);
                                                color: rgb(254 226 226);
                                            }
                                        }
                                        @keyframes pulse-green {
                                            0%, 100% {
                                                background-color: rgb(34 197 94);
                                                color: rgb(255 255 255);
                                            }
                                            50% {
                                                background-color: rgb(22 163 74);
                                                color: rgb(240 253 244);
                                            }
                                        }
                                    `}</style>
                                    {isTransferring ? 'Transferring...' : 'Take Over'}
                                </button>
                                <button
                                    className={`font-medium px-4 py-2 rounded-lg shadow-lg ${
                                        status === 'ended' || isTransferring
                                            ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                                            : handoffStage === 'guidance'
                                            ? 'bg-yellow-500 text-white cursor-wait'
                                            : handoffStage === 'ready'
                                            ? 'bg-green-500 hover:bg-green-600 text-white'
                                            : 'bg-green-500 hover:bg-green-600 text-white'
                                    }`}
                                    style={{
                                        animation: status !== 'ended' && !isTransferring && !handoffStage ? 'pulse-green 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' : 'none'
                                    }}
                                    onClick={handleHandoff}
                                    disabled={status === 'ended' || isTransferring || handoffStage === 'guidance'}
                                >
                                    {handoffStage === 'guidance' ? 'Waiting for customer...' : 
                                     handoffStage === 'ready' ? 'Complete Hand Off' : 
                                     'Hand Off'}
                                </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LiveCallView;